import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { parseMenuConfig } from '../utils/menu';
import { useAuthStore } from '../store/authStore';
import { useReunion } from '../contexts/ReunionContext';
import { NotificationBell } from './notifications/NotificationBell';
import { AUTH_NAV, getVisibleNav } from '../config/navigation';
import { DesktopDropdown } from './header/DesktopDropdown';
import { MobileAccordion } from './header/MobileAccordion';
import { UserMenu } from './header/UserMenu';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const menuItems = parseMenuConfig();
  const isHomePage = location.pathname === '/';
  const siteName = import.meta.env.VITE_SITE_NAME || 'KG ProDesign';

  const { user, roles, clearAuth } = useAuthStore();
  const isAdminUser = roles.some(r => r.roleSlug === 'super_admin' || r.roleSlug === 'reunion_admin');
  const { activeReunionId, reunion } = useReunion();

  const visibleNav = user ? getVisibleNav(AUTH_NAV, reunion?.featureToggles) : [];
  const revealEnabled = reunion?.featureToggles?.reunion2027RevealEnabled === true;

  const nameParts = siteName.split(' ');
  const firstWord = nameParts[0];
  const restWords = nameParts.slice(1).join(' ');

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  const isActive = (path: string) => {
    if (path === '/' || path === '') return location.pathname === '/';
    const normalized = path.startsWith('/') ? path : `/${path}`;
    return location.pathname === normalized;
  };

  const showTransparent = isHomePage && !isScrolled;
  const headerClasses = showTransparent
    ? 'bg-transparent border-transparent'
    : 'bg-white/90 backdrop-blur-md border-b border-slate-200';
  const logoColor = showTransparent ? 'text-white' : 'text-slate-900';
  const linkColor = showTransparent
    ? 'text-white/90 hover:text-white'
    : 'text-slate-600 hover:text-primary';
  const activeLinkColor = showTransparent ? 'text-white font-bold' : 'text-primary';

  const renderNavLink = (item: { title: string; path: string; external?: boolean }, mobile = false) => {
    const active = isActive(item.path);
    const baseClasses = mobile
      ? `block text-base font-semibold transition-colors ${active ? 'text-primary' : 'text-slate-600 hover:text-primary'}`
      : `text-sm font-semibold transition-colors ${active ? activeLinkColor : linkColor}`;

    if (item.external) {
      return (
        <a key={item.path} href={item.path} target="_blank" rel="noopener noreferrer" className={baseClasses}>
          {item.title}
        </a>
      );
    }

    const to = item.path.startsWith('/') ? item.path : `/${item.path}`;
    return (
      <Link key={item.path} to={to} className={baseClasses} onClick={mobile ? () => setIsMobileMenuOpen(false) : undefined}>
        {item.title}
      </Link>
    );
  };

  const handleLogout = () => {
    clearAuth();
    setIsMobileMenuOpen(false);
  };

  // Build user menu items
  const userMenuItems = user ? [
    { label: 'Account', path: '/account', icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" /></svg> },
    ...(activeReunionId ? [{ label: 'Notifications', path: '/inbox', icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" /></svg> }] : []),
    ...(isAdminUser && activeReunionId ? [{ label: 'Admin Panel', path: '/admin', icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg> }] : []),
  ] : [];

  return (
    <header className={`sticky top-0 z-50 w-full transition-all duration-300 ${headerClasses}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="bg-primary p-1.5 rounded-lg text-white">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <span className={`text-xl font-bold tracking-tight uppercase transition-colors duration-300 ${logoColor}`}>
              {firstWord}
              {restWords && <span className="text-primary">{restWords}</span>}
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8">
            {!user ? (
              <>
                {/* About, then Reunion 2027, then the rest of the env menu (Contact). */}
                {menuItems.slice(0, 1).map(item => renderNavLink(item))}
                {revealEnabled && (
                  <Link to="/reunion-2027" className={`text-sm font-semibold transition-colors ${linkColor}`}>Reunion 2027</Link>
                )}
                {menuItems.slice(1).map(item => renderNavLink(item))}
                <Link to="/register" className={`text-sm font-semibold transition-colors ${linkColor}`}>Sign Up</Link>
                <Link to="/login" className={`text-sm font-semibold transition-colors ${linkColor}`}>Login</Link>
              </>
            ) : (
              <>
                {/* Dashboard first, then env items (except last), then grouped dropdowns, then last env item (Contact) */}
                {visibleNav.map((entry) =>
                  entry.kind === 'link' ? (
                    renderNavLink({ title: entry.item.label, path: entry.item.path, external: entry.item.external })
                  ) : null
                )}
                {menuItems.slice(0, -1).map(item => renderNavLink(item))}
                {visibleNav.map((entry) =>
                  entry.kind === 'group' ? (
                    <DesktopDropdown
                      key={entry.group.label}
                      group={entry.group}
                      linkColor={linkColor}
                      activeLinkColor={activeLinkColor}
                    />
                  ) : null
                )}
                {menuItems.length > 0 && renderNavLink(menuItems[menuItems.length - 1])}
              </>
            )}
          </nav>

          {/* Right side: CTA / NotificationBell / UserMenu / Hamburger */}
          <div className="flex items-center gap-3">
            {user && activeReunionId && (
              <NotificationBell
                reunionId={activeReunionId}
                className={`hidden md:block ${showTransparent ? 'text-white/80 hover:text-white' : ''}`}
              />
            )}
            {!user ? (
              <Link
                to="/register"
                className="hidden md:inline-flex bg-primary text-white px-6 py-2.5 rounded-lg font-bold text-sm hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
              >
                Sign Up
              </Link>
            ) : (
              <div className="hidden md:block">
                <UserMenu
                  firstName={user.firstName || ''}
                  lastName={user.lastName || ''}
                  items={userMenuItems}
                  onLogout={handleLogout}
                  transparent={showTransparent}
                />
              </div>
            )}
            <button
              className="md:hidden p-2 -mr-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                <svg className={`w-6 h-6 transition-colors ${showTransparent ? 'text-white' : 'text-slate-600'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className={`w-6 h-6 transition-colors ${showTransparent ? 'text-white' : 'text-slate-600'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu panel */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-slate-200 px-4 py-6 space-y-4 shadow-lg">
          {!user ? (
            <>
              {menuItems.slice(0, 1).map(item => renderNavLink(item, true))}
              {revealEnabled && renderNavLink({ title: 'Reunion 2027', path: '/reunion-2027' }, true)}
              {menuItems.slice(1).map(item => renderNavLink(item, true))}
              <Link to="/register" className="block w-full text-center bg-primary text-white px-6 py-2.5 rounded-lg font-bold text-sm hover:bg-primary/90 transition-all shadow-lg shadow-primary/20 mt-4" onClick={() => setIsMobileMenuOpen(false)}>
                Sign Up
              </Link>
              <Link to="/login" className="block w-full text-center border border-primary text-primary px-6 py-2.5 rounded-lg font-bold text-sm hover:bg-primary/10 transition-all" onClick={() => setIsMobileMenuOpen(false)}>
                Login
              </Link>
            </>
          ) : (
            <>
              {/* Dashboard + top-level links first */}
              {visibleNav.map((entry) =>
                entry.kind === 'link' ? (
                  renderNavLink({ title: entry.item.label, path: entry.item.path, external: entry.item.external }, true)
                ) : null
              )}
              {/* Env items (except last) */}
              {menuItems.slice(0, -1).map(item => renderNavLink(item, true))}
              {/* Grouped dropdowns as accordions */}
              {visibleNav.map((entry) =>
                entry.kind === 'group' ? (
                  <MobileAccordion
                    key={entry.group.label}
                    group={entry.group}
                    onNavigate={() => setIsMobileMenuOpen(false)}
                  />
                ) : null
              )}
              {/* Last env item (Contact) */}
              {menuItems.length > 0 && renderNavLink(menuItems[menuItems.length - 1], true)}
              {/* User section */}
              <div className="border-t border-slate-200 pt-4 mt-4 space-y-3">
                <Link to="/account" className="block text-base font-semibold text-slate-600 hover:text-primary transition-colors" onClick={() => setIsMobileMenuOpen(false)}>
                  Account
                </Link>
                {activeReunionId && (
                  <Link to="/inbox" className="block text-base font-semibold text-slate-600 hover:text-primary transition-colors" onClick={() => setIsMobileMenuOpen(false)}>
                    Notifications
                  </Link>
                )}
                {isAdminUser && activeReunionId && (
                  <Link to="/admin" className="block text-base font-semibold text-slate-600 hover:text-primary transition-colors" onClick={() => setIsMobileMenuOpen(false)}>
                    Admin Panel
                  </Link>
                )}
                <button onClick={handleLogout} className="block w-full text-left text-base font-semibold text-slate-500 hover:text-red-600 transition-colors">
                  Sign Out
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </header>
  );
};
